import { useEffect, useState } from "react";
import { Flag, Layout, ArrowLeftRight, Vote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useLocation } from "react-router-dom";

// Define the nav button variants using cva
const navButtonVariants = cva(
  "flex items-center space-x-3 w-full transition-all duration-200",
  {
    variants: {
      variant: {
        default:
          "hover:bg-[#C0E6FF]/30 hover:backdrop-blur-md focus-visible:ring-2 focus-visible:ring-ring",
        selected:
          "hover:bg-[#C0E6FF]/30 bg-[#C0E6FF]/30 backdrop-blur-md border border-white/20 rounded-sm shadow-lg relative transform translate-x-5 shadow-lg after:absolute after:left-0 after:top-0 after:h-full after:w-1 after:bg-primary",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

// Type for the nav item click handler
export type NavItemClickHandler = (id: string) => void;

// Interface for nav items
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
}

// Props interface for the LeftNav component
export interface LeftNavProps extends VariantProps<typeof navButtonVariants> {
  /** Callback fired when a nav item is selected */
  onSelect?: NavItemClickHandler;
  /** Currently selected nav item ID */
  selectedId?: string;
  /** Default selected nav item ID (for uncontrolled usage) */
  defaultSelectedId?: string;
  /** Additional class names */
  className?: string;
  buttonStyleOverrides?: string;
}

// Check if vote page should be enabled
const isVotePageEnabled = () => {
  const enabledTimestamp = import.meta.env.VITE_VOTE_PAGE_ENABLED_TIMESTAMP;
  const isEnabled = !enabledTimestamp || Date.now() >= parseInt(enabledTimestamp);
  return isEnabled;
};

const baseNavItems: NavItem[] = [
  {
    id: "overview",
    label: "Overview",
    icon: <Layout className="h-5 w-5" />,
  },
  {
    id: "checkpoints",
    label: "Checkpoints",
    icon: <Flag className="h-5 w-5" />,
  },
  {
    id: "transaction-blocks",
    label: "Transaction Blocks",
    icon: <ArrowLeftRight className="h-5 w-5" />,
  },
];

const voteNavItem: NavItem = {
  id: "vote",
  label: "Governance Vote",
  icon: <Vote className="h-5 w-5" />,
};

// Conditionally add vote item
const navItems: NavItem[] = isVotePageEnabled()
  ? [...baseNavItems, voteNavItem]
  : baseNavItems;

export function LeftNav({
  onSelect,
  selectedId: controlledSelectedId,
  defaultSelectedId = "overview",
  className,
  buttonStyleOverrides,
}: LeftNavProps) {
  const location = useLocation();

  // Support both controlled and uncontrolled modes
  const [internalSelectedId, setInternalSelectedId] = useState<string>(defaultSelectedId);
  const selectedId = controlledSelectedId ?? internalSelectedId;

  const handleSelect = (id: string) => {
    if (!controlledSelectedId) {
      setInternalSelectedId(id);
    }
    onSelect?.(id);
  };

  // Only sync with URL when in uncontrolled mode
  useEffect(() => {
    if (!controlledSelectedId) {
      const path = location.pathname.replace("/", "");
      const section = path === "" ? "overview" : path;
      setInternalSelectedId(section);
    }
  }, [location.pathname, controlledSelectedId]);

  return (
    <nav className={cn("flex flex-col space-y-5 w-64 px-2 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/30", className)}>
      {navItems.map((item) => (
        <Button
          key={item.id}
          variant="ghost"
          className={cn(
            navButtonVariants({
              variant: selectedId === item.id ? "selected" : "default",
            }), "justify-start", buttonStyleOverrides
          )}
          onClick={() => handleSelect(item.id)}
        >
          <span className="text-current">{item.icon}</span>
          <span className="text-lg font-normal">{item.label}</span>
        </Button>
      ))}
    </nav>
  );
}